import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Lock, Mail, MoreVertical, UserX } from 'lucide-react';
import { cancelAvailability } from './inscritosVocabulary';
import type { Fase, RegistrationEntry } from './inscritosVocabulary';

export interface InscritoRowActionsProps {
  row: RegistrationEntry;
  fase: Fase;
  /** Lifts the cancel request to the parent. This component issues NO HTTP call itself. */
  onCancelRequest: (row: RegistrationEntry) => void;
}

const MENU_ITEM_CLASSNAME =
  'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm text-ink hover:bg-surface-hover';

// Tallest menu variant (fase "depois": two plain entries + divider + the
// disabled block with its hint line) measures ~167px live — 185 leaves a
// safety margin without needing a second estimate for the shorter fase
// "antes" menu.
const ESTIMATED_MENU_HEIGHT = 185;

/**
 * Walks up from the row's own root to the nearest ancestor that actually
 * clips overflow — e.g. InscritosTable's horizontally-scrollable wrapper,
 * which the CSS overflow spec forces into `overflow-y: auto` too the moment
 * `overflow-x` stops being `visible`. Confirmed live: without this, the menu
 * for a row near the bottom of a full table page rendered ~90% below that
 * ancestor's clipping edge — invisible, not just scrolled.
 */
function findClippingAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    if (window.getComputedStyle(node).overflowY !== 'visible') return node;
    node = node.parentElement;
  }
  return null;
}

function decideOpenDirection(root: HTMLElement | null): 'down' | 'up' {
  const clippingAncestor = root && findClippingAncestor(root);
  if (!root || !clippingAncestor) return 'down';
  const rootBottom = root.getBoundingClientRect().bottom;
  const boundaryBottom = clippingAncestor.getBoundingClientRect().bottom;
  return rootBottom + ESTIMATED_MENU_HEIGHT > boundaryBottom ? 'up' : 'down';
}

export function InscritoRowActions({ row, fase, onCancelRequest }: InscritoRowActionsProps): JSX.Element {
  const navigate = useNavigate();
  const availability = cancelAvailability(row, fase);
  const [open, setOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState<'down' | 'up'>('down');
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Fechar clicando fora e no Escape — mesmo par mousedown+ref/keydown que
  // NotificationBell.tsx e ProfileMenu.tsx já usam. Sem o trap de foco e sem
  // roving tabindex de propósito: este menu nasce e morre por linha de
  // tabela (8+ por página), a ordem nativa de Tab pelos <button>/<a> reais
  // já basta.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const goToRespostas = () => {
    setOpen(false);
    navigate(`/resultado-simulado/${row.registrationId}`);
  };

  return (
    <div ref={rootRef} className="relative flex items-center gap-1">
      {/*
        `durante` trata a janela como já fechada, igual a `depois`
        (design.md de `simulado-cancelamento-efetivo`, Decisão 1) — por isso
        o corte é `fase !== 'antes'`, não uma comparação com `'depois'`.
      */}
      {fase !== 'antes' ? (
        <button
          type="button"
          onClick={goToRespostas}
          className="inline-flex h-8 items-center gap-1.5 rounded-input border border-edge-strong bg-surface px-3 text-[13px] font-medium text-ink hover:bg-surface-hover"
        >
          <Eye size={15} aria-hidden />
          Respostas
        </button>
      ) : availability.available ? (
        <button
          type="button"
          onClick={() => onCancelRequest(row)}
          className="inline-flex h-8 items-center gap-1.5 rounded-input border border-danger/40 bg-surface px-3 text-[13px] font-medium text-danger-ink hover:bg-danger/[0.08]"
        >
          <UserX size={15} aria-hidden />
          Cancelar
        </button>
      ) : null}

      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Mais ações"
        onClick={() => {
          if (!open) setOpenDirection(decideOpenDirection(rootRef.current));
          setOpen((previous) => !previous);
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-input text-ink-secondary hover:bg-surface-hover"
      >
        <MoreVertical size={16} aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 z-10 w-60 rounded-card border border-edge bg-surface p-1.5 shadow-panel ${
            openDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <button type="button" role="menuitem" onClick={goToRespostas} className={MENU_ITEM_CLASSNAME}>
            <Eye size={16} className="shrink-0" aria-hidden />
            Ver respostas
          </button>

          <a
            role="menuitem"
            href={`mailto:${row.userEmail}`}
            onClick={() => setOpen(false)}
            className={MENU_ITEM_CLASSNAME}
          >
            <Mail size={16} className="shrink-0" aria-hidden />
            Enviar e-mail
          </a>

          <div role="separator" className="my-1 border-t border-edge" />

          {availability.available ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onCancelRequest(row);
              }}
              className={MENU_ITEM_CLASSNAME}
            >
              <UserX size={16} className="shrink-0" aria-hidden />
              Cancelar inscrição
            </button>
          ) : (
            <div
              role="menuitem"
              aria-disabled="true"
              className="flex cursor-not-allowed flex-col gap-1 rounded-md px-2.5 py-2"
            >
              <div className="flex items-center gap-2.5 text-ink-muted">
                <UserX size={16} className="shrink-0" aria-hidden />
                <span className="text-sm">Cancelar inscrição</span>
                <span className="flex-1" />
                <Lock size={14} className="shrink-0" aria-hidden />
              </div>
              <p className="m-0 pl-[26px] text-xs text-ink-muted">{availability.justification}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default InscritoRowActions;
